/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React, { useState } from "react";
import { Box, Typography, useTheme, Tooltip } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import ThumbDownAltRoundedIcon from "@mui/icons-material/ThumbDownAltRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { accentFor, STEP_SUCCESS, STEP_SUCCESS_SOFT, SHAPE } from "../model/tokens";
import { useAssistant } from "../AssistantContext";

/** A suggested-navigation button the model offers alongside its answer. */
function NavSuggestion({ nav, accent }) {
  const theme = useTheme();
  const { navigateTo } = useAssistant();
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => navigateTo(nav.path)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigateTo(nav.path)}
      sx={{
        mt: 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1.25,
        cursor: "pointer",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.border.color}`,
        backgroundColor: theme.palette.secondary.main,
        transition: "border-color 0.15s ease, background-color 0.15s ease",
        "&:hover": { borderColor: accent },
      }}
    >
      <LinkRoundedIcon sx={{ fontSize: 18, color: accent }} />
      <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600, color: accent }}>
        Navigate to {nav.label}
      </Typography>
      <ChevronRightRoundedIcon sx={{ fontSize: 20, color: accent }} />
    </Box>
  );
}

/** "N steps completed" — collapsed by default, expands to the tool-call list. */
function AgentStepChip({ steps }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  if (!steps || steps.length === 0) return null;
  const done = steps.filter((s) => s.status !== "failed").length;

  return (
    <Box sx={{ mb: 0.75 }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          cursor: "pointer",
          px: 1,
          py: 0.4,
          borderRadius: 999,
          color: STEP_SUCCESS,
          backgroundColor: STEP_SUCCESS_SOFT,
          fontSize: 12,
          fontWeight: 500,
          userSelect: "none",
        }}
      >
        <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />
        {done} step{done === 1 ? "" : "s"} completed
      </Box>
      {open && (
        <Box sx={{ mt: 0.75, pl: 0.5, display: "flex", flexDirection: "column", gap: 0.4 }}>
          {steps.map((s, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {s.status === "failed" ? (
                <ErrorOutlineRoundedIcon sx={{ fontSize: 13, color: theme.palette.error?.main || "#CF4E36" }} />
              ) : (
                <CheckCircleRoundedIcon sx={{ fontSize: 13, color: STEP_SUCCESS }} />
              )}
              <Typography sx={{ fontSize: 12, color: theme.palette.secondaryText.color }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/** Copy · 👍 · 👎 on assistant turns. */
function MessageActions({ message }) {
  const theme = useTheme();
  const { rate } = useAssistant();
  const [copied, setCopied] = useState(false);
  const accent = accentFor(theme);

  const btn = {
    p: 0.5,
    display: "inline-flex",
    cursor: "pointer",
    borderRadius: 1,
    color: theme.palette.secondaryText.color,
    "&:hover": { color: theme.palette.text.color },
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {
      /* clipboard blocked — silent */
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, ml: 4.5 }}>
      <Tooltip title={copied ? "Copied" : "Copy"} arrow>
        <Box sx={btn} onClick={copy}>
          <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />
        </Box>
      </Tooltip>
      <Tooltip title="Good response" arrow>
        <Box
          sx={{ ...btn, color: message.rating === "up" ? accent : btn.color }}
          onClick={() => rate(message.id, "up")}
        >
          <ThumbUpAltRoundedIcon sx={{ fontSize: 15 }} />
        </Box>
      </Tooltip>
      <Tooltip title="Bad response" arrow>
        <Box
          sx={{ ...btn, color: message.rating === "down" ? theme.palette.error?.main || "#CF4E36" : btn.color }}
          onClick={() => rate(message.id, "down")}
        >
          <ThumbDownAltRoundedIcon sx={{ fontSize: 15 }} />
        </Box>
      </Tooltip>
    </Box>
  );
}

export default function MessageBubble({ message }) {
  const theme = useTheme();
  const accent = accentFor(theme);
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Box
          sx={{
            maxWidth: "82%",
            px: 1.5,
            py: 1,
            borderRadius: SHAPE.bubbleRadiusUser,
            backgroundColor: accent,
            color: "#fff",
            fontSize: 14,
            lineHeight: 1.5,
            wordBreak: "break-word",
          }}
        >
          {message.content}
        </Box>
      </Box>
    );
  }

  const hasBody = message.content || message.streaming || message.error;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <Box
          aria-hidden
          sx={{
            width: 30,
            height: 30,
            borderRadius: "9px",
            flex: "none",
            display: "grid",
            placeItems: "center",
            background: "linear-gradient(135deg, #3B6EF6, #7C5CFC)",
            color: "#fff",
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 17 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <AgentStepChip steps={message.steps} />

          {message.error ? (
            <Box
              sx={{
                border: `1px solid ${theme.palette.error?.main || "#CF4E36"}`,
                backgroundColor: "rgba(207,78,54,0.08)",
                borderRadius: 2,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography sx={{ fontSize: 13, color: theme.palette.text.color }}>
                {message.error.message}
              </Typography>
            </Box>
          ) : (
            hasBody && (
              <Box
                sx={{
                  border: `1px solid ${theme.palette.border.color}`,
                  backgroundColor: theme.palette.tertiary.main,
                  borderRadius: SHAPE.bubbleRadius,
                  px: 1.5,
                  py: 1,
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: theme.palette.text.color,
                  wordBreak: "break-word",
                  "& > *:first-of-type": { mt: 0 },
                  "& > *:last-child": { mb: 0 },
                  "& p": { my: 0.75 },
                  "& h1,& h2,& h3,& h4,& h5,& h6": { fontWeight: 600, lineHeight: 1.3, mt: 1.5, mb: 0.5 },
                  "& h1": { fontSize: 18 },
                  "& h2": { fontSize: 16 },
                  "& h3": { fontSize: 15 },
                  "& h4,& h5,& h6": { fontSize: 14 },
                  "& ul,& ol": { pl: 2.5, my: 0.75 },
                  "& li": { mb: 0.25 },
                  "& a": { color: accent, textDecoration: "underline" },
                  "& code": {
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "0.85em",
                    backgroundColor: theme.palette.secondary.main,
                    border: `1px solid ${theme.palette.border.color}`,
                    borderRadius: "4px",
                    px: 0.5,
                    py: "1px",
                  },
                  "& pre": {
                    backgroundColor: theme.palette.secondary.main,
                    border: `1px solid ${theme.palette.border.color}`,
                    borderRadius: "8px",
                    p: 1.5,
                    overflow: "auto",
                    my: 1,
                  },
                  "& pre code": { border: "none", backgroundColor: "transparent", p: 0 },
                  "& table": { borderCollapse: "collapse", width: "100%", my: 1, fontSize: 13 },
                  "& th,& td": {
                    border: `1px solid ${theme.palette.border.color}`,
                    px: 1,
                    py: 0.5,
                    textAlign: "left",
                  },
                  "& blockquote": {
                    borderLeft: `3px solid ${theme.palette.border.color}`,
                    pl: 1.5,
                    ml: 0,
                    my: 1,
                    color: theme.palette.secondaryText.color,
                  },
                  "& hr": {
                    border: "none",
                    borderTop: `1px solid ${theme.palette.border.color}`,
                    my: 1.5,
                  },
                  "& img": { maxWidth: "100%" },
                }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ node, children, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
                {message.streaming && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: "7px",
                      height: "1em",
                      ml: "2px",
                      verticalAlign: "text-bottom",
                      backgroundColor: accent,
                      borderRadius: "1px",
                      animation: "vcsBlink 1s steps(2,start) infinite",
                      "@keyframes vcsBlink": { "50%": { opacity: 0 } },
                    }}
                  />
                )}
              </Box>
            )
          )}
        </Box>
      </Box>

      {!message.streaming && message.nav && (
        <NavSuggestion nav={message.nav} accent={accent} />
      )}

      {!message.streaming && !message.error && message.content && (
        <MessageActions message={message} />
      )}
    </Box>
  );
}
